import { pickCss } from '@/shared/lib/css-utils';
import { Text } from '@/shared/ui';
import { useAgentPanelStore } from '../model/use-agent-panel-store';
import css from './workflow-stepper.module.scss';

const stepClass = pickCss(css, css.step);
const indicatorClass = pickCss(css, css.indicator);

type StepStatus = 'pending' | 'active' | 'done' | 'skipped';

const computeStepStatus = (
  index: number,
  activeIndex: number,
  skipped: ReadonlyArray<number>,
  workflowDone: boolean
): StepStatus => {
  if (skipped.includes(index)) return 'skipped';
  if (workflowDone) return 'done';
  if (index < activeIndex) return 'done';
  if (index === activeIndex) return 'active';
  return 'pending';
};

export type WorkflowStepperProps = {
  // Disable the Skip button while a network request is in flight.
  isPending?: boolean;
  onSkipStep?: () => void | Promise<void>;
};

export const WorkflowStepper = ({
  isPending = false,
  onSkipStep,
}: WorkflowStepperProps) => {
  const ws = useAgentPanelStore((s) => s.workflowSession);
  if (!ws) return null;

  const workflowDone = ws.phase === 'workflow-done';
  const showSkip =
    !workflowDone && ws.phase !== 'workflow-cancelled' && !!onSkipStep;

  return (
    <nav aria-label="Workflow progress" className={css.root}>
      <Text className={css.title} size="xs" variant="muted" weight="semibold">
        {ws.workflow.replace(/_/g, ' ')}
      </Text>
      <ol className={css.steps}>
        {ws.steps.map((step, index) => {
          const status = computeStepStatus(
            index,
            ws.activeStepIndex,
            ws.skippedStepIndices,
            workflowDone
          );
          const isActiveSkippable =
            status === 'active' && step.skippable && showSkip;
          return (
            <li
              aria-current={status === 'active' ? 'step' : undefined}
              className={stepClass({
                'is-active': status === 'active',
                'is-done': status === 'done',
                'is-skipped': status === 'skipped',
              })}
              key={`${index}-${step.command}`}
            >
              <span
                aria-hidden="true"
                className={indicatorClass({
                  'is-active': status === 'active',
                  'is-done': status === 'done',
                  'is-skipped': status === 'skipped',
                })}
              >
                {renderIndicator(status, index)}
              </span>
              <Text className={css.label} size="sm" weight="medium">
                {step.label}
              </Text>
              {isActiveSkippable && (
                <button
                  className={css.skipButton}
                  disabled={isPending}
                  onClick={() => void onSkipStep?.()}
                  type="button"
                >
                  Skip
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const renderIndicator = (status: StepStatus, index: number): string => {
  if (status === 'done') return '✓';
  if (status === 'skipped') return '–';
  return String(index + 1);
};
